/** Matches backend EmailVerificationEmailRateLimitWindow (1 minute). */
export const VERIFICATION_RESEND_COOLDOWN_SECONDS = 60;

/** Tab-scoped so signup / login / verify-email share one countdown. */
export const VERIFICATION_RESEND_COOLDOWN_STORAGE_KEY =
  "api-portal.verification-resend-cooldown-until";

export function formatVerificationResendCooldownLabel(
  secondsRemaining: number,
): string {
  if (secondsRemaining <= 0) {
    return "Resend verification email";
  }
  return `Resend available in ${secondsRemaining}s`;
}

function readCooldownUntilMs(): number | null {
  try {
    const raw = window.sessionStorage.getItem(
      VERIFICATION_RESEND_COOLDOWN_STORAGE_KEY,
    );
    if (!raw) {
      return null;
    }
    const until = Number(raw);
    if (!Number.isFinite(until)) {
      window.sessionStorage.removeItem(VERIFICATION_RESEND_COOLDOWN_STORAGE_KEY);
      return null;
    }
    return until;
  } catch {
    return null;
  }
}

/** Seconds left on the shared resend cooldown (0 when idle or expired). */
export function getVerificationResendCooldownRemaining(
  nowMs = Date.now(),
): number {
  const until = readCooldownUntilMs();
  if (until === null) {
    return 0;
  }
  const remainingMs = until - nowMs;
  if (remainingMs <= 0) {
    try {
      window.sessionStorage.removeItem(VERIFICATION_RESEND_COOLDOWN_STORAGE_KEY);
    } catch {
      // Ignore storage errors (private mode / quota).
    }
    return 0;
  }
  return Math.ceil(remainingMs / 1000);
}

/** Starts (or restarts) the shared cooldown; returns seconds remaining. */
export function startVerificationResendCooldown(
  cooldownSeconds = VERIFICATION_RESEND_COOLDOWN_SECONDS,
  nowMs = Date.now(),
): number {
  const until = nowMs + cooldownSeconds * 1000;
  try {
    window.sessionStorage.setItem(
      VERIFICATION_RESEND_COOLDOWN_STORAGE_KEY,
      String(until),
    );
  } catch {
    // Ignore storage errors; UI still counts down in-memory for this mount.
  }
  return cooldownSeconds;
}

/** Test helper — clears the shared cooldown. */
export function clearVerificationResendCooldown(): void {
  try {
    window.sessionStorage.removeItem(VERIFICATION_RESEND_COOLDOWN_STORAGE_KEY);
  } catch {
    // Ignore storage errors.
  }
}
